import {
    checkClaimsWeb,
    generateID,
    getJsonKeyFromField,
    isListTypeSection,
    sortFields,
} from '@eventr/common'
import {
    ColoursObj,
    ErrorWrapper,
    Gutters,
    ImageRenderer,
    MainButton,
    MobileConfirmDialog,
    TDialogStore,
    TImageVariants,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import _ from 'lodash'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import { HeaderSearchBar } from '../../home/components/carousel/slide/footer/HeaderSearchBar'
import i18n from '../../i18n/i18next'
import { bs, useBoundStore } from '../../state/bound.store'
import { query } from '../../state/headerState/headerSignals'
import { defaultAddSnack } from '../../state/overlayState/overlayContext'
import { useThemeContext } from '../../state/themeState/themeContext'
import { useEphemeralStore } from '../../state/useEphemeralStore'
import { RendererLocator } from '../../v2/renderers/rendererLocator'
import { ScreenTypeProvider } from '../../v2/state/screenTypeProvider'
import { HotelEditAdvert } from '../hotelBooking/hotelEditAdvert'
import { NavigationView } from '../layout/NavigationView'
import { useEditSectionContext } from './editSectionProvider'

const EmptyBookings = ({ section, src }: { section: string; src: string }) => {
    return <ImageRenderer variant={section as TImageVariants} src={src} />
}

export const EditSection = () => {
    const { sectionData, section, createNewItem, getItinTitle, deleteItem } =
        useEditSectionContext()
    const themeImages = useThemeContext((s) => s.themeImages)
    const { sectionID } = useParams<{ sectionID: string }>()
    const [sID, type] = (sectionID ?? '').split('-')
    // sectionDataID is the one to use
    const sectionDataID = parseInt(sID)

    const { setShowBookingService } = useEphemeralStore(
        useShallow((s) => ({
            setShowBookingService: s.setShowBookingService,
        }))
    )
    const { orgConfig, getFieldsForSection, selectedItinID } = useBoundStore(
        useShallow((s) => ({
            orgConfig: s.orgConfig,
            selectedItinID: s.selectedItinID,
            getFieldsForSection: s.getFieldsForSection,
        }))
    )
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const filteredElements = useMemo(() => {
        if (sectionData && section) {
            const sectionCp = _.cloneDeep(sectionData)
            const sectionDatacp = sectionCp.data
            let bookings = section?.properties?.jsonName
                ? sectionDatacp[section?.properties?.jsonName]
                : undefined
            bookings = bookings?.filter((booking: { _og_code: any }) => {
                if (booking._og_code) return false
                if (section.id === 8 && type === 'todo') return false
                const string = JSON.stringify(booking)
                return string
                    .toLocaleLowerCase()
                    .includes(query.value.toLocaleLowerCase())
            })
            if (section.properties?.jsonName) {
                sectionCp.data[section?.properties?.jsonName] = bookings
            }

            return sectionCp
        }
        return undefined
    }, [sectionData, section, type])

    useEffect(() => {
        setShowBookingService(orgConfig)
    }, [setShowBookingService, orgConfig])

    const isAnyData = useMemo(() => {
        const sortedFields = sortFields(getFieldsForSection(sectionDataID))

        const isListType = isListTypeSection(sortedFields)
        if (isListType) {
            const json_key = getJsonKeyFromField(sortedFields[0].field)

            if (!sectionData || sectionData.data[json_key] === undefined) {
                return false
            }
            if (sectionData.data[json_key].length === 0) {
                return false
            }
        }

        return true
    }, [sectionData, sectionDataID, getFieldsForSection])

    const sectionEditSettings = useMemo(() => {
        if (section === undefined) {
            return undefined
        }
        return section.properties?.mobileEditFieldOptions
    }, [section])

    const onBackPressed = () => {
        query.value = ''
        window.goBack()
    }

    if (!sectionEditSettings) {
        return <div />
    }

    const addItem = () => {
        if (!section) {
            return
        }
        const _id = generateID(selectedItinID, section.id)
        createNewItem?.(_id)

        window.pushPath('/edit/Section/' + section.id + '/' + _id)
    }

    const deleteAllitems = async () => {
        if (!sectionData || !section) {
            return
        }
        const dataObj = sectionData.data
        const bookingArr = dataObj[section.properties?.jsonName ?? '']?.map(
            (booking: { _id: any }) => {
                return booking._id
            }
        )
        let deletionSuccesful = true

        const resp = await bs().deleteItineraryItem(
            section.id,
            bookingArr.join('-')
        )

        if (!resp) {
            clearDialog()
            defaultAddSnack('Did not delete correctly', 'error')
            deletionSuccesful = false
        } else {
            deleteItem?.(bookingArr)
        }

        if (deletionSuccesful) {
            clearDialog()
        }
    }

    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={
                    i18n.t('edit-itinerary.section.edit-section', {
                        sectionName: i18n.t(
                            `sections.${section?.properties?.jsonName}`,
                            { count: 0 }
                        ),
                    }) ?? 'Edit or Add Items'
                }
                navigationBarSubtitle={getItinTitle?.()}
                backButtonAction={onBackPressed}
                navigationBarTrailing={[
                    {
                        id: 'moreButtonEditSection',
                        icon: 'More',
                        popoverItems: [
                            {
                                title: i18n.t(
                                    'edit-itinerary.section.delete-all',
                                    {
                                        sectionName: i18n.t(
                                            `sections.${section?.properties?.jsonName}`,
                                            { count: 0 }
                                        ),
                                    }
                                ),
                                action: () => {
                                    replaceDialog({
                                        node: (
                                            <MobileConfirmDialog
                                                header={i18n.t(
                                                    'edit-itinerary.section.delete-all',
                                                    {
                                                        sectionName: i18n.t(
                                                            `sections.${section?.properties?.jsonName}`,
                                                            { count: 0 }
                                                        ),
                                                    }
                                                )}
                                                text={i18n.t(
                                                    'edit-itinerary.section.delete-all-check',
                                                    {
                                                        sectionName: i18n.t(
                                                            `sections.${section?.properties?.jsonName}`,
                                                            { count: 0 }
                                                        ),
                                                    }
                                                )}
                                                onConfirm={deleteAllitems}
                                                onCancel={() => {
                                                    clearDialog()
                                                }}
                                                cancelText={i18n.t(
                                                    'general.cancel'
                                                )}
                                                confirmText={i18n.t(
                                                    'general.delete'
                                                )}
                                                confirmBtnStyle="destructiveBorderless"
                                            />
                                        ),
                                        backdropClosable: false,
                                    })
                                },
                                color: ColoursObj.states.error,
                                active:
                                    isAnyData &&
                                    checkClaimsWeb(
                                        [`/${selectedItinID}`],
                                        ['deleteItinerary']
                                    ),
                            },
                        ],
                    },
                ]}
            >
                <Stack>
                    <>
                        <Gutters>
                            <Box display={'grid'} gap={'8px'}>
                                {isAnyData && <HeaderSearchBar />}

                                <MainButton
                                    id={`Add ${section?.name ?? ''}-button`}
                                    text={
                                        i18n.t(
                                            'edit-itinerary.section.add-new',
                                            {
                                                sectionName: i18n.t(
                                                    `sections.${section?.properties?.jsonName}`,
                                                    { count: 1 }
                                                ),
                                            }
                                        ) ?? 'Add New'
                                    }
                                    onClick={addItem}
                                    icon="Add"
                                    iconPosition="left"
                                    style={{
                                        boxShadow:
                                            '0px 4px 8px 0px rgba(51, 82, 111, 0.10)',
                                    }}
                                />
                            </Box>
                        </Gutters>
                        {sectionData?.sectionID === 3 && <HotelEditAdvert />}
                        {!isAnyData ? (
                            <Stack alignItems="center">
                                <EmptyBookings
                                    section={section?.properties?.jsonName ?? ''}
                                    src={
                                        themeImages[section?.properties?.jsonName ?? '']
                                    }
                                />
                                <Typography
                                    align="center"
                                    variant="bodyBold"
                                    color={ColoursObj.core.primary}
                                >
                                    {i18n.t(
                                        'edit-itinerary.section.empty-message',
                                        {
                                            count: 0,
                                            sectionName: i18n.t(
                                                `sections.${section?.properties?.jsonName}`,
                                                { count: 0 }
                                            ),
                                        }
                                    )}
                                </Typography>
                            </Stack>
                        ) : null}

                        <Stack marginX="8px">
                            <ErrorWrapper>
                                <ScreenTypeProvider screenType="edit">
                                    {filteredElements && (<RendererLocator
                                        data={filteredElements}
                                        screenType="edit"
                                    />)}

                                </ScreenTypeProvider>
                            </ErrorWrapper>
                        </Stack>
                    </>

                    <div />
                    <Stack gap="16px">
                        {section && section.id === 1 && (
                            <MainButton
                                text={i18n.t('general.edit')}
                                onClick={() => {
                                    window.pushPath(
                                        '/edit/Section/' + section.id + '/0'
                                    )
                                }}
                            />
                        )}
                    </Stack>
                </Stack>
            </NavigationView>
        </>
    )
}
